export type ClientExperiment = {
  name: string;
  cohorts: { name: string; weight: number }[];
  isActive: boolean;
  isEnrolling?: boolean;
  parent?: { name: string; cohort: string };
  attribute?: string;
  prepaintTimeoutMs?: number;
};

export type ClientExperimentRuntime = {
  resolve: (name: string) => string | null;
  subscribe: (listener: () => void) => () => void;
  ready: (name: string, cohort: string) => boolean;
  expose: (name: string, track: (name: string, cohort: string) => void) => void;
};

export const EXPERIMENT_SEED_COOKIE = 'wistia_experiment_seed';
export const EXPERIMENT_STORAGE_KEY = 'wistia_client_experiments';
export const EXPERIMENT_SEED_MAX_AGE = 365 * 24 * 60 * 60;

// A literal keeps the blocking script independent of bundler-generated helpers.
// React reads this same runtime rather than implementing another assignment path.
const runtimeSource = String.raw`(function(config,cookieName,storageKey,maxAge){
  var registry=Object.create(null),records=Object.create(null),overrides=Object.create(null);
  var pending=Object.create(null),disabled=Object.create(null),listeners=[],seed=null;
  var uuid=/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  var own=function(o,k){return Object.prototype.hasOwnProperty.call(o,k);};
  function readSeed(){
    try{
      var values=document.cookie.split(';').map(function(part){return part.trim().split('=');}).filter(function(pair){return pair[0]===cookieName;});
      var value=values.length===1?values[0].slice(1).join('='):null;
      return uuid.test(value)?value:null;
    }catch(e){return null;}
  }
  function currentIdentity(){return seed!==null&&readSeed()===seed;}
  function key(kind,name){return storageKey+':'+seed+':'+kind+':'+name;}
  function read(kind,name){
    if(!currentIdentity())return null;
    try{return JSON.parse(localStorage.getItem(key(kind,name)));}catch(e){return null;}
  }
  function write(kind,name,value){
    if(!currentIdentity())return;
    try{
      if(value===null)localStorage.removeItem(key(kind,name));
      else localStorage.setItem(key(kind,name),JSON.stringify(value));
    }catch(e){}
  }
  function validCohort(experiment,cohort){return experiment.cohorts.some(function(c){return c.name===cohort;});}
  function participation(name){
    var record=read('participation',name);
    if(record&&typeof record==='object'&&validCohort(registry[name],record.cohort))records[name]=record;
    return records[name];
  }
  function bucket(value){
    var hash=2166136261;
    for(var i=0;i<value.length;i++)hash=Math.imul(hash^value.charCodeAt(i),16777619);
    hash^=hash>>>16;hash=Math.imul(hash,0x85ebca6b);
    hash^=hash>>>13;hash=Math.imul(hash,0xc2b2ae35);hash^=hash>>>16;
    return (hash>>>0)%10000;
  }
  function hasTarget(name){return !!document.querySelector('[data-client-experiment="'+name+'"]');}
  function resolve(name){
    var experiment=registry[name];
    if(!experiment||!experiment.isActive||disabled[name]||!currentIdentity())return null;
    if(pending[name]&&Date.now()>=pending[name].deadline&&hasTarget(name))return null;
    if(experiment.parent&&(pending[experiment.parent.name]||resolve(experiment.parent.name)!==experiment.parent.cohort))return null;
    var cohort=null;
    if(own(overrides,name)&&validCohort(experiment,overrides[name]))cohort=overrides[name];
    else{
      var existing=participation(name);
      if(existing)cohort=existing.cohort;
      else if(experiment.isEnrolling!==false){
        var position=bucket(seed+':'+name),end=0;
        for(var i=0;i<experiment.cohorts.length;i++){
          end+=experiment.cohorts[i].weight;
          if(position<end){cohort=experiment.cohorts[i].name;break;}
        }
      }
    }
    return cohort;
  }
  function overridden(name){
    if(own(overrides,name)&&validCohort(registry[name],overrides[name]))return true;
    var parent=registry[name]&&registry[name].parent;
    return !!parent&&overridden(parent.name);
  }
  function applyAttributes(){
    config.forEach(function(experiment){
      if(experiment.attribute){
        var cohort=resolve(experiment.name);
        if(cohort===null)document.documentElement.removeAttribute(experiment.attribute);
        else document.documentElement.setAttribute(experiment.attribute,cohort);
      }
    });
  }
  function publish(){
    applyAttributes();
    listeners.slice().forEach(function(listener){listener();});
  }
  function enforceDeadlines(){
    var changed=false;
    Object.keys(pending).forEach(function(name){
      if(Date.now()>=pending[name].deadline){
        if(!hasTarget(name)&&document.readyState==='loading')return;
        clearTimeout(pending[name].timer);delete pending[name];
        if(hasTarget(name))disabled[name]=true;
        changed=true;
      }
    });
    if(changed)publish();
  }
  function finishParsing(){
    var changed=false;
    Object.keys(pending).forEach(function(name){
      if(!hasTarget(name)){clearTimeout(pending[name].timer);delete pending[name];changed=true;}
    });
    if(changed)publish();
    enforceDeadlines();
  }
  function ready(name,cohort){
    enforceDeadlines();
    if(!currentIdentity()||resolve(name)!==cohort)return false;
    if(pending[name]){
      clearTimeout(pending[name].timer);delete pending[name];publish();
    }
    return true;
  }
  function expose(name,track){
    enforceDeadlines();
    var cohort=resolve(name);
    if(cohort===null||!currentIdentity()||overridden(name)||pending[name])return;
    var previous=participation(name);
    if(previous&&previous.cohort===cohort&&previous.tracked===true)return;
    if(!currentIdentity())return;
    records[name]={cohort:cohort,tracked:true};write('participation',name,records[name]);
    if(currentIdentity())track(name,cohort);
  }
  function subscribe(listener){
    listeners.push(listener);
    return function(){listeners=listeners.filter(function(item){return item!==listener;});};
  }
  config.forEach(function(experiment){registry[experiment.name]=experiment;});
  seed=readSeed();
  try{
    if(!seed&&window.crypto){
      if(typeof window.crypto.randomUUID==='function')seed=window.crypto.randomUUID();
      else if(typeof window.crypto.getRandomValues==='function'){
        var bytes=new Uint8Array(16);window.crypto.getRandomValues(bytes);
        bytes[6]=(bytes[6]&15)|64;bytes[8]=(bytes[8]&63)|128;
        var hex=Array.prototype.map.call(bytes,function(b){return ('0'+b.toString(16)).slice(-2);}).join('');
        seed=hex.slice(0,8)+'-'+hex.slice(8,12)+'-'+hex.slice(12,16)+'-'+hex.slice(16,20)+'-'+hex.slice(20);
      }
      if(seed&&uuid.test(seed)){
        var host=location.hostname;
        var domain=host==='wistia.com'||host.slice(-11)==='.wistia.com'?'; Domain=.wistia.com':host==='wistia.st'||host.slice(-10)==='.wistia.st'?'; Domain=.wistia.st':'';
        document.cookie=cookieName+'='+seed+'; Path=/; Max-Age='+maxAge+'; SameSite=Lax'+domain+(location.protocol==='https:'?'; Secure':'');
        if(readSeed()!==seed)seed=null;
      }else seed=null;
    }
  }catch(e){seed=null;}
  var params=new URLSearchParams(location.search);
  config.forEach(function(experiment){
    var saved=read('override',experiment.name);
    if(validCohort(experiment,saved))overrides[experiment.name]=saved;
    var forced=params.get(experiment.name);
    if(forced==='reset'){
      delete overrides[experiment.name];write('override',experiment.name,null);
    }else if(validCohort(experiment,forced)){
      overrides[experiment.name]=forced;write('override',experiment.name,forced);
    }
  });
  window.__wistiaExperiments={resolve:resolve,expose:expose,subscribe:subscribe,ready:ready};
  var timed=config.filter(function(experiment){return experiment.prepaintTimeoutMs&&resolve(experiment.name)!==null;});
  timed.forEach(function(experiment){
    pending[experiment.name]={deadline:Date.now()+experiment.prepaintTimeoutMs,timer:setTimeout(enforceDeadlines,experiment.prepaintTimeoutMs)};
  });
  if(Object.keys(pending).length){
    if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',finishParsing,{once:true});
    else finishParsing();
  }
  applyAttributes();
})`;

const validateExperiments = (experiments: ClientExperiment[]) => {
  const registry = new Map<string, ClientExperiment>();
  const attributes = new Set<string>();
  for (const experiment of experiments) {
    if (
      !/^[a-z][a-z0-9_]*$/.test(experiment.name) ||
      registry.has(experiment.name) ||
      !experiment.cohorts.length ||
      new Set(experiment.cohorts.map((cohort) => cohort.name)).size !==
        experiment.cohorts.length ||
      experiment.cohorts.some(
        (cohort) =>
          !/^[a-z][a-z0-9_]*$/.test(cohort.name) ||
          cohort.name === 'reset' ||
          !Number.isInteger(cohort.weight) ||
          cohort.weight <= 0,
      ) ||
      experiment.cohorts.reduce((sum, cohort) => sum + cohort.weight, 0) !==
        10000 ||
      (experiment.attribute &&
        (!/^data-[a-z][a-z0-9-]*$/.test(experiment.attribute) ||
          attributes.has(experiment.attribute))) ||
      (experiment.prepaintTimeoutMs !== undefined &&
        (!experiment.attribute ||
          !Number.isInteger(experiment.prepaintTimeoutMs) ||
          experiment.prepaintTimeoutMs <= 0))
    ) {
      throw new Error(`Invalid client experiment: ${experiment.name}`);
    }
    registry.set(experiment.name, experiment);
    if (experiment.attribute) attributes.add(experiment.attribute);
  }
  for (const experiment of experiments) {
    const seen = new Set<string>([experiment.name]);
    let parent = experiment.parent;
    while (parent) {
      const ancestor = registry.get(parent.name);
      if (
        !ancestor ||
        seen.has(parent.name) ||
        !ancestor.cohorts.some((cohort) => cohort.name === parent.cohort)
      ) {
        throw new Error(
          `Invalid parent for client experiment: ${experiment.name}`,
        );
      }
      seen.add(parent.name);
      parent = ancestor.parent;
    }
  }
};

export const buildClientExperimentsScript = (
  experiments: ClientExperiment[],
): string => {
  validateExperiments(experiments);
  const config = JSON.stringify([
    experiments,
    EXPERIMENT_SEED_COOKIE,
    EXPERIMENT_STORAGE_KEY,
    EXPERIMENT_SEED_MAX_AGE,
  ])
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
  return `${runtimeSource}.apply(null,${config});`;
};

export const getClientExperimentRuntime =
  (): ClientExperimentRuntime | null => {
    if (typeof window === 'undefined') return null;
    return (
      (window as Window & { __wistiaExperiments?: ClientExperimentRuntime })
        .__wistiaExperiments ?? null
    );
  };

export type ClientExperimentDefinition = Omit<ClientExperiment, 'cohorts'> & {
  cohorts: { name: string; percentage: number }[];
};

export const defineClientExperiment = (
  definition: ClientExperimentDefinition,
): ClientExperiment => {
  const cohorts = definition.cohorts.map(({ name, percentage }) => {
    const weight = Math.round(percentage * 100);
    if (
      !Number.isFinite(percentage) ||
      weight <= 0 ||
      percentage > 100 ||
      Math.abs(percentage * 100 - weight) > 1e-8
    ) {
      throw new Error(
        `Invalid percentage for ${definition.name}/${name}: use a positive percentage with at most two decimal places`,
      );
    }
    return { name, weight };
  });
  if (cohorts.reduce((sum, cohort) => sum + cohort.weight, 0) !== 10000) {
    throw new Error(`Percentages for ${definition.name} must total 100`);
  }
  return { ...definition, cohorts };
};
